import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import axios from "axios";
import { ArrowLeft, MapPin, Percent, Clock, Tag, Globe, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getMapsUrl = (b) => {
  if (b?.latitude && b?.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${b.latitude},${b.longitude}`;
  }
  const q = [b?.name, b?.address].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

const BusinessDetailPage = () => {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBusiness();
  }, [id]);

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${id}`);
      setBusiness(response.data);
    } catch (error) {
      console.error("Failed to fetch business:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F6] flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-[#F8F8F6] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-['Playfair_Display'] text-[#020617] mb-4">Business not found</h2>
          <Link to="/businesses">
            <Button className="btn-gold">Back to Directory</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F6]">
      {/* Navigation */}
      <nav className="bg-[#020617] border-b border-[#D4AF37]/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/businesses" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Directory</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
              <span className="text-[#020617] font-bold text-lg tracking-tight">A+</span>
            </div>
          </Link>
        </div>
      </nav>

      {/* Hero Image */}
      <div className="h-80 bg-[#020617] relative overflow-hidden">
        {business.image_url ? (
          <img
            src={business.image_url}
            alt={business.name}
            className="w-full h-full object-cover opacity-60"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#020617]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] to-transparent" />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 -mt-32 relative z-10 pb-16">
        <div className="bg-white rounded-2xl shadow-luxury p-8" data-testid="business-detail">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
            <div>
              <span className="text-[#D4AF37] text-xs font-medium tracking-wider uppercase">
                {business.category}
              </span>
              <h1 className="font-['Playfair_Display'] text-4xl text-[#020617] mt-2">
                {business.name}
              </h1>
            </div>
            <div className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#047857] text-white font-bold text-lg">
              <Percent className="w-5 h-5" />
              <span>{business.discount_level}% OFF</span>
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-600 text-lg leading-relaxed mb-8">
            {business.description}
          </p>

          {/* Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <a
              href={getMapsUrl(business)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group"
              data-testid="business-maps-link"
            >
              <MapPin className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div className="flex-1">
                <p className="font-medium text-[#020617] flex items-center gap-1.5">
                  Location
                  <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#D4AF37] transition-colors" />
                </p>
                <p className="text-gray-500 mt-1">{business.address}</p>
                <p className="text-[#D4AF37] text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Open in Google Maps</p>
              </div>
            </a>
            <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50">
              <Tag className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-[#020617]">Category</p>
                <p className="text-gray-500 mt-1 capitalize">{business.category}</p>
              </div>
            </div>
          </div>

          {/* 3D Virtual Tour */}
          {business.tour_3d_url && (
            <div className="mb-8">
              <a 
                href={business.tour_3d_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-[#D4AF37]/10 to-[#047857]/10 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors group"
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[#020617]">Virtual Tour Available</p>
                  <p className="text-gray-500 text-sm">Explore this business in 3D</p>
                </div>
                <ExternalLink className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          )}

          {/* How to redeem */}
          <div className="bg-[#020617] rounded-xl p-6 text-white">
            <h3 className="font-['Playfair_Display'] text-xl mb-4">How to Redeem</h3>
            <ol className="space-y-3 text-white/70">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-[#020617] flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <span>Show your Advanta Plus digital membership card to staff</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-[#020617] flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <span>Staff will scan your unique QR code to verify your membership</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-[#020617] flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <span>Enjoy your {business.discount_level}% discount on your purchase</span>
              </li>
            </ol>
          </div>

          {/* Location mini map — stays in-tab, no Google Maps redirect */}
          {(business.latitude && business.longitude) || business.address ? (
            <div className="mt-8">
              <h3 className="font-['Playfair_Display'] text-xl text-[#020617] mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#D4AF37]" /> Location
              </h3>
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <iframe
                  title={`Map of ${business.name}`}
                  src={
                    business.latitude && business.longitude
                      ? `https://maps.google.com/maps?q=${business.latitude},${business.longitude}&z=16&output=embed`
                      : `https://maps.google.com/maps?q=${encodeURIComponent([business.name, business.address].filter(Boolean).join(", "))}&z=16&output=embed`
                  }
                  width="100%"
                  height="260"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          ) : null}

          {/* CTA */}
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/dashboard">
              <Button className="btn-gold px-8 py-4 rounded" data-testid="show-membership-btn">
                Show My Membership
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetailPage;
